import type { ComponentProps, FC, FocusEvent } from 'react';
import { FilteredSelect, FormField, Inline, Paragraph } from '@george-labs.com/design-system';
import type { ItemPropTypes } from '@george-labs.com/design-system/types/components/Dropdown';
import type { FORM_FIELD_LABEL_POSITION } from '@george-labs.com/design-system/types/components/Form';

import { useField } from '@luna/form2';

import type { FormFieldProps } from './types/FormFieldProps';
import { shouldShowError } from './FieldErrorVisibilityService';

interface Props extends FormFieldProps {
    label: string;
    emptyLabel?: string;
    items: ({ 'data-cy'?: string } & ItemPropTypes)[];
    isLoading?: boolean;
    loadingText?: string;
    clearable?: boolean;
    required?: boolean;
    disabled?: boolean;
    multiple?: boolean;
    highlightText?: boolean;
    onBlur?: ComponentProps<typeof FilteredSelect>['onBlur'];
    onFilter?: ComponentProps<typeof FilteredSelect>['onFilter'];
    onSelect?: ComponentProps<typeof FilteredSelect>['onSelect'];
    allowAny?: boolean;
    isErrorVisible?: boolean;
    shouldClearOnUnmount?: boolean;
    renderItem?: ComponentProps<typeof FilteredSelect>['renderItem'];
    actionButton?: ComponentProps<typeof FilteredSelect>['actionButton'];
    selectedItemsText?: string;
}

interface FormFilteredSelectFieldStatics {
    LABEL_POSITION: typeof FORM_FIELD_LABEL_POSITION;
}

const LABEL_COLUMN_WIDTH = 4;

export const FormFilteredSelectField: FC<Props> & FormFilteredSelectFieldStatics = ({
    'data-cy': dataCy,
    fieldName,
    items,
    label,
    labelPosition,
    placeholder,
    description,
    alert,
    warning,
    info,
    emptyLabel,
    labelColumnWidth = LABEL_COLUMN_WIDTH,
    required = false,
    disabled = false,
    clearable = false,
    isLoading = false,
    loadingText,
    multiple = false,
    highlightText = false,
    onBlur,
    onFilter,
    onSelect,
    allowAny,
    isErrorVisible = true,
    addon,
    shouldClearOnUnmount,
    renderItem,
    actionButton,
    showErrorBeforeBlur,
    className,
    compact,
    selectedItemsText,
}) => {
    const [{ value, onBlur: onFieldBlur }, { error, touched, dirty }, { setValue }] = useField<
        string | string[]
    >(fieldName, { shouldClearOnUnmount });
    const isFieldErrorVisible = shouldShowError(
        isErrorVisible,
        touched,
        error,
        showErrorBeforeBlur,
        dirty,
    );

    const handleSelect = (selectedItem: ItemPropTypes<string> | ItemPropTypes<string>[]) => {
        onSelect?.(selectedItem);
        if (Array.isArray(selectedItem)) {
            setValue(selectedItem.map((item) => item.value));
        } else {
            setValue(selectedItem.value);
        }
    };
    const handleClear = () => setValue('');
    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
        onBlur?.(e);
        onFieldBlur(e);
    };

    return (
        <FormField
            label={label && <Inline data-cy={`${dataCy}-label`}>{label}</Inline>}
            labelPosition={labelPosition}
            labelColumnWidth={labelColumnWidth}
            placeholder={placeholder}
            alert={alert}
            description={description}
            warning={warning}
            info={info}
            required={required}
            disabled={disabled}
            error={
                isFieldErrorVisible && <Paragraph data-cy={`${dataCy}-error`}>{error}</Paragraph>
            }
            input={
                <FilteredSelect<ItemPropTypes<string>>
                    actionButton={actionButton}
                    name={fieldName}
                    value={value}
                    data-cy={dataCy}
                    items={items}
                    emptyLabel={emptyLabel}
                    clearable={clearable}
                    isLoading={isLoading}
                    loadingText={loadingText}
                    multiple={multiple}
                    highlightText={highlightText}
                    onSelect={handleSelect}
                    onClear={handleClear}
                    onBlur={handleBlur}
                    onFilter={onFilter}
                    allowAny={allowAny}
                    renderItem={renderItem}
                    selectedItemsText={selectedItemsText}
                />
            }
            addon={addon}
            compact={compact}
            className={className}
        />
    );
};

FormFilteredSelectField.LABEL_POSITION = FormField.LABEL_POSITION;
